import { supabase } from '@/shared/lib/supabase';
import { logger } from '@/shared/lib/logger';
import { NotFoundError, ValidationError } from '@/shared/lib/errors';
import { Money, centsToWire } from '@shared/money';
import { computeSplit, validatePayers } from '@shared/split';
import type { CurrencyCode } from '@shared/currencies';
import {
  expenseSchema,
  expensePayerSchema,
  expenseParticipantSchema,
  type Expense,
  type ExpenseCreateInput,
  type ExpensePayer,
  type ExpenseParticipant,
} from '@shared/schemas/expenses';

// ==================== Query keys ====================

export const expensesKeys = {
  all: ['expenses'] as const,
  byGroup: (gid: string) => [...expensesKeys.all, 'group', gid] as const,
  byId: (id: string) => [...expensesKeys.all, 'id', id] as const,
  history: (id: string) => [...expensesKeys.all, 'id', id, 'history'] as const,
  comments: (id: string) => [...expensesKeys.all, 'id', id, 'comments'] as const,
};

// ==================== Fetch ====================

export interface ExpenseWithRelations extends Expense {
  payers: ExpensePayer[];
  participants: ExpenseParticipant[];
  category_name: string;
  category_icon: string;
}

// Select compartido para listar gastos con sus relaciones embebidas.
const EXPENSE_LIST_SELECT = `
  *,
  category:categories!inner(name, icon),
  payers:expense_payers(*, participant:participants(display_name, avatar_url)),
  participants:expense_participants(*)
`;

// Convierte una fila cruda de PostgREST al shape con relaciones. Devuelve null
// si la fila no valida (para no tumbar todo el listado por una fila corrupta).
function toExpenseWithRelations(row: unknown): ExpenseWithRelations | null {
  try {
    const parsed = expenseSchema.parse(row);
    const cat = (row as { category: { name: string; icon: string } }).category;
    return {
      ...parsed,
      category_name: cat.name,
      category_icon: cat.icon,
      payers: ((row as { payers: unknown[] }).payers ?? []).map((p) => expensePayerSchema.parse(p)),
      participants: ((row as { participants: unknown[] }).participants ?? []).map((p) =>
        expenseParticipantSchema.parse(p)
      ),
    };
  } catch (e) {
    logger.error('fetchGroupExpenses: fila inválida omitida', {
      id: (row as { id?: string })?.id,
      message: e instanceof Error ? e.message : String(e),
    });
    return null;
  }
}

// Lista los gastos de un grupo. `limit` acota la consulta — la vista de grupo
// solo muestra unos pocos, y traer los miles de gastos de un grupo grande (con
// payers/participants embebidos) en una sola petición la hacía fallar. Sin
// `limit` trae todos (usar solo con paginación por rango en su lugar).
export async function fetchGroupExpenses(
  groupId: string,
  limit?: number,
): Promise<ExpenseWithRelations[]> {
  let query = supabase
    .from('expenses')
    .select(EXPENSE_LIST_SELECT)
    .eq('group_id', groupId)
    .is('deleted_at', null)
    .order('expense_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (typeof limit === 'number') query = query.limit(limit);

  const { data, error } = await query;

  if (error) {
    logger.error('fetchGroupExpenses failed', { code: error.code, groupId });
    throw new Error(error.message);
  }

  return (data ?? [])
    .map(toExpenseWithRelations)
    .filter((e): e is ExpenseWithRelations => e !== null);
}

/** Por qué columna se ordena la lista de gastos. */
export type ExpensesSortKey = 'date' | 'amount' | 'merchant';

export interface ExpensesPageOptions {
  sort: ExpensesSortKey;
  dir: 'asc' | 'desc';
  /** `null` = todas las categorías. */
  categoryId: string | null;
}

export const ORDEN_POR_DEFECTO: ExpensesPageOptions = {
  sort: 'date',
  dir: 'desc',
  categoryId: null,
};

/** La columna real de cada criterio. `merchant` ordena por el nombre tecleado. */
const COLUMNA: Record<ExpensesSortKey, string> = {
  date: 'expense_date',
  amount: 'amount_cents',
  merchant: 'merchant_name_raw',
};

// Página de gastos de un grupo (paginación por rango en servidor). Para la vista
// "Ver todos" de grupos con miles de movimientos: evita traerlos todos de golpe.
//
// El orden y el filtro van **en la consulta**, no sobre las filas ya traídas.
// Ordenar en el navegador sólo reordenaría los 20 de la página actual, con lo
// que «el gasto más caro» sería el más caro de esos veinte: una respuesta falsa
// con toda la apariencia de ser la buena.
export async function fetchGroupExpensesPage(
  groupId: string,
  page: number,
  pageSize: number,
  opciones: ExpensesPageOptions = ORDEN_POR_DEFECTO,
): Promise<{ rows: ExpenseWithRelations[]; total: number }> {
  const from = page * pageSize;
  const to = from + pageSize - 1;
  const asc = opciones.dir === 'asc';

  let query = supabase
    .from('expenses')
    .select(EXPENSE_LIST_SELECT, { count: 'exact' })
    .eq('group_id', groupId)
    .is('deleted_at', null);

  if (opciones.categoryId) query = query.eq('category_id', opciones.categoryId);

  query = query.order(COLUMNA[opciones.sort], { ascending: asc });
  // Desempate estable. Sin él, dos gastos del mismo día (o del mismo importe)
  // pueden salir en distinto orden en cada petición, y al pasar de página una
  // fila se repite mientras otra no aparece nunca.
  if (opciones.sort !== 'date') query = query.order('expense_date', { ascending: false });
  query = query.order('created_at', { ascending: false });

  const { data, error, count } = await query.range(from, to);

  if (error) {
    logger.error('fetchGroupExpensesPage failed', { code: error.code, groupId });
    throw new Error(error.message);
  }

  const rows = (data ?? [])
    .map(toExpenseWithRelations)
    .filter((e): e is ExpenseWithRelations => e !== null);
  return { rows, total: count ?? rows.length };
}

export async function fetchExpense(expenseId: string): Promise<ExpenseWithRelations> {
  const { data, error } = await supabase
    .from('expenses')
    .select(
      `
      *,
      category:categories!inner(name, icon),
      payers:expense_payers(*, participant:participants(display_name, avatar_url)),
      participants:expense_participants(*)
    `
    )
    .eq('id', expenseId)
    .is('deleted_at', null)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) throw new NotFoundError('Expense', expenseId);

  const parsed = expenseSchema.parse(data);
  const cat = (data as unknown as { category: { name: string; icon: string } }).category;
  return {
    ...parsed,
    category_name: cat.name,
    category_icon: cat.icon,
    payers: ((data as unknown as { payers: unknown[] }).payers ?? []).map((p) =>
      expensePayerSchema.parse(p)
    ),
    participants: ((data as unknown as { participants: unknown[] }).participants ?? []).map((p) =>
      expenseParticipantSchema.parse(p)
    ),
  };
}

// ==================== Create / Update ====================

/**
 * Construye los argumentos del RPC transaccional `expense_write`.
 *
 * El REPARTO se calcula aquí (`computeSplit`, con su residuo determinista) y
 * viaja ya resuelto, para que el algoritmo viva en un único sitio compartido.
 * Todo lo demás —resolver comercio y categoría, tipo de cambio, y las cinco
 * escrituras— lo hace el RPC dentro de UNA transacción.
 *
 * Antes esto eran 8 pasos sueltos desde el cliente: si fallaba uno intermedio
 * quedaba un gasto sin pagadores o sin participantes, incoherente y contando en
 * balances. Además `updateExpense` resolvía comercio/categoría con su propia
 * lógica inline, saltándose el matching difuso y la cuarentena de import.
 */
function buildExpenseWriteArgs(
  expenseId: string,
  input: ExpenseCreateInput,
  isUpdate: boolean,
) {
  const amountCents = BigInt(input.amount_cents.toString());
  const total = Money.fromCents(amountCents, input.currency as CurrencyCode);

  const payersNormalized = input.payers.map((p) => ({
    participant_id: p.participant_id,
    amount_paid_cents: BigInt(p.amount_paid_cents.toString()),
  }));
  const payerValidation = validatePayers(payersNormalized, total);
  if (!payerValidation.valid) {
    throw new ValidationError(payerValidation.message ?? 'Pagadores inválidos');
  }

  const shares = computeSplit({
    expense_id: expenseId,
    total,
    mode: input.split_mode,
    participants: input.participants.map((p) =>
      p.share_value !== null && p.share_value !== undefined
        ? { participant_id: p.participant_id, share_value: p.share_value }
        : { participant_id: p.participant_id }
    ),
    payers: payersNormalized,
  });

  const overrideIn = input.amount_group_currency_override_cents;
  const hasOverride = overrideIn !== null && overrideIn !== undefined;

  // Los parámetros opcionales se OMITEN cuando no hay valor: su DEFAULT en el
  // RPC ya es NULL, y así el objeto encaja con los tipos generados del esquema.
  return {
    p_expense_id: expenseId,
    p_is_update: isUpdate,
    p_group_id: input.group_id,
    p_merchant_name: input.merchant_name.trim(),
    p_amount_cents: centsToWire(amountCents),
    p_currency: input.currency,
    p_split_mode: input.split_mode,
    p_expense_date: input.expense_date,
    p_tags: input.tags ?? [],
    p_is_import: input.is_import ?? false,
    p_payers: payersNormalized.map((p) => ({
      participant_id: p.participant_id,
      amount_paid_cents: centsToWire(p.amount_paid_cents),
    })),
    p_participants: shares.map((s) => ({
      participant_id: s.participant_id,
      share_value: s.share_value,
      amount_owed_cents: centsToWire(s.amount_owed_cents),
    })),
    ...(input.merchant_id ? { p_merchant_id: input.merchant_id } : {}),
    ...(input.category_id ? { p_category_id: input.category_id } : {}),
    ...(input.category_name ? { p_category_name: input.category_name } : {}),
    ...(input.category_icon ? { p_category_icon: input.category_icon } : {}),
    ...(input.description ? { p_description: input.description } : {}),
    ...(hasOverride
      ? {
          p_override_cents: centsToWire(BigInt(overrideIn.toString())),
          ...(input.amount_group_currency_override_ccy
            ? { p_override_ccy: input.amount_group_currency_override_ccy }
            : {}),
        }
      : {}),
  };
}

/**
 * Crea un gasto con sus pagadores y participantes, de forma atómica.
 *
 * El `id` se genera en cliente porque entra en el hash que elige el destinatario
 * del residuo de redondeo (`chooseResidueTarget`) y porque hace la creación
 * idempotente: reintentar una petición que ya se persistió no duplica el gasto.
 */
export async function createExpense(input: ExpenseCreateInput): Promise<Expense> {
  const expenseId = crypto.randomUUID();
  const { data, error } = await supabase.rpc(
    'expense_write',
    buildExpenseWriteArgs(expenseId, input, false),
  );
  if (error) {
    logger.error('createExpense failed', { code: error.code, message: error.message });
    throw new Error(error.message);
  }
  return expenseSchema.parse(data);
}

/**
 * Actualiza un gasto existente. Reemplaza pagadores y participantes completos
 * dentro de la misma transacción. El historial lo registra el trigger
 * `log_expense_change`; `created_by` no cambia.
 */
export async function updateExpense(
  expenseId: string,
  input: ExpenseCreateInput,
): Promise<Expense> {
  const { data, error } = await supabase.rpc(
    'expense_write',
    buildExpenseWriteArgs(expenseId, input, true),
  );
  if (error) {
    logger.error('updateExpense failed', { code: error.code, message: error.message });
    throw new Error(error.message);
  }
  return expenseSchema.parse(data);
}


// ==================== Delete (soft) ====================

export async function softDeleteExpense(expenseId: string): Promise<void> {
  const { error } = await supabase
    .from('expenses')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', expenseId);
  if (error) throw new Error(error.message);
}

// ==================== Category suggestion by merchant ====================

/**
 * Sprint F.1: llama la RPC `suggest_category_for_merchant` para obtener la
 * categoría más usada para un merchant en un grupo. Devuelve NULL si no hay
 * history o si el RPC falla.
 */
export async function suggestCategoryForMerchant(
  groupId: string,
  merchantId: string | null,
  merchantNameRaw: string | null,
): Promise<string | null> {
  const { data, error } = await supabase.rpc('suggest_category_for_merchant', {
    p_group_id: groupId,
    ...(merchantId ? { p_merchant_id: merchantId } : {}),
    ...(merchantNameRaw ? { p_merchant_name_raw: merchantNameRaw } : {}),
  });
  if (error) {
    logger.error('suggest_category_for_merchant failed', { code: error.code });
    return null;
  }
  return (data as string | null) ?? null;
}

// ==================== Suggested workflow ====================

/**
 * Aceptar una sugerencia: pasa `is_suggested` a false → entra en balances.
 * El expense YA existe, así que esto es sólo un UPDATE.
 */
export async function acceptSuggestedExpense(expenseId: string): Promise<void> {
  const { error } = await supabase
    .from('expenses')
    .update({ is_suggested: false })
    .eq('id', expenseId);
  if (error) throw new Error(error.message);
}

/**
 * Descartar una sugerencia: soft-delete, no cuenta en nada.
 * Equivalente funcional a `softDeleteExpense` pero con nombre semántico.
 */
export const dismissSuggestedExpense = softDeleteExpense;

// ==================== Bulk operations (Sprint J) ====================

/**
 * Bulk soft-delete: marca `deleted_at = now()` en múltiples expenses de una.
 * RLS del delete individual (ver 20260713... expenses) aplica per-row —
 * si el user no tiene permisos sobre alguno, ese row NO se actualiza pero
 * los demás sí. Devuelve el count real actualizado.
 */
export async function bulkSoftDeleteExpenses(expenseIds: string[]): Promise<number> {
  if (expenseIds.length === 0) return 0;
  const { data, error } = await supabase
    .from('expenses')
    .update({ deleted_at: new Date().toISOString() })
    .in('id', expenseIds)
    .is('deleted_at', null)
    .select('id');
  if (error) {
    logger.error('bulkSoftDeleteExpenses failed', { code: error.code });
    throw new Error(error.message);
  }
  return (data ?? []).length;
}

/**
 * Bulk change category: aplica el mismo `category_id` a N expenses.
 * Útil tras un import CSV donde todos tienen `category_id = null`.
 */
export async function bulkSetExpensesCategory(
  expenseIds: string[],
  categoryId: string,
): Promise<number> {
  if (expenseIds.length === 0) return 0;
  const { data, error } = await supabase
    .from('expenses')
    .update({ category_id: categoryId })
    .in('id', expenseIds)
    .is('deleted_at', null)
    .select('id');
  if (error) {
    logger.error('bulkSetExpensesCategory failed', { code: error.code });
    throw new Error(error.message);
  }
  return (data ?? []).length;
}
